// src/components/maintenance/components/ProcessedView.js
import React, { useState } from 'react';
import { useRecordContext, useNotify, useRefresh } from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Restore as RestoreIcon } from '@mui/icons-material';
import { rollbackMaintenanceOperation } from '../utils/maintenanceProcessor';

const ProcessedView = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [rolling, setRolling] = useState(false);

  const handleRollback = async () => {
    try {
      setRolling(true);

      await rollbackMaintenanceOperation(record.id);

      notify('Rollback completed successfully', { type: 'success' });
      setRollbackDialogOpen(false);
      refresh();

    } catch (error) {
      console.error('Rollback error:', error);
      notify(`Error during rollback: ${error.message}`, { type: 'error' });
    } finally {
      setRolling(false);
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              Changes have been successfully processed and committed to the database.
            </Typography>
          </Alert>

          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>

          {record.preview_data && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                • New students added: <strong>{record.preview_data.students?.new?.length || 0}</strong>
              </Typography>
              <Typography variant="body2">
                • Students updated: <strong>{record.preview_data.students?.updated?.length || 0}</strong>
              </Typography>
              <Typography variant="body2">
                • Students moved to alumni: <strong>{record.preview_data.students?.graduating?.length || 0}</strong>
              </Typography>
              <Typography variant="body2">
                • New teachers added: <strong>{record.preview_data.teachers?.new?.length || 0}</strong>
              </Typography>
              <Typography variant="body2">
                • Teachers updated: <strong>{record.preview_data.teachers?.updated?.length || 0}</strong>
              </Typography>
              <Typography variant="body2">
                • New classes added: <strong>{record.preview_data.classes?.new?.length || 0}</strong>
              </Typography>
              <Typography variant="body2">
                • Classes updated: <strong>{record.preview_data.classes?.updated?.length || 0}</strong>
              </Typography>
              {record.preview_data.totalRows && (
                <>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <em>Total rows in uploaded CSVs:</em>
                  </Typography>
                  <Typography variant="body2">
                    • Teachers CSV: <strong>{record.preview_data.totalRows.teachers || 0}</strong> rows
                  </Typography>
                  <Typography variant="body2">
                    • Classes CSV: <strong>{record.preview_data.totalRows.classes || 0}</strong> rows
                  </Typography>
                  <Typography variant="body2">
                    • Students CSV: <strong>{record.preview_data.totalRows.students || 0}</strong> rows
                  </Typography>
                </>
              )}
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Danger Zone
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              If something went wrong, you can rollback to the state before this maintenance job.
              This will restore all classes, teachers, students and parents to their previous state.
            </Alert>
            <Button
              variant="outlined"
              color="error"
              startIcon={<RestoreIcon />}
              onClick={() => setRollbackDialogOpen(true)}
            >
              Rollback Changes
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Rollback Confirmation Dialog */}
      <Dialog
        open={rollbackDialogOpen}
        onClose={() => !rolling && setRollbackDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Rollback</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to rollback? This will restore the database to the state
            before this maintenance job was processed.
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackDialogOpen(false)} disabled={rolling}>
            Cancel
          </Button>
          <Button
            onClick={handleRollback}
            color="error"
            variant="contained"
            disabled={rolling}
          >
            {rolling ? 'Rolling back...' : 'Confirm Rollback'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProcessedView;